import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
// material
import { styled } from '@material-ui/core/styles';
import { Box, Drawer } from '@material-ui/core';
// components
import Logo from '../../components/Logo';
import Scrollbar from '../../components/Scrollbar';
import NavSection from '../../components/NavSection';
import { MHidden } from '../../components/@material-extend';
//
import getSidebarConfig from './SidebarConfig';
import LoginBox from '../../components/authentication/login/LoginBox';
import VersionFooter from '../../components/nav/VersionFooter';
import { useIsFeatureFlagEnabled } from '../../utils/FeatureFlags';
// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    width: DRAWER_WIDTH
  }
}));

// ----------------------------------------------------------------------

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func
};

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }) {
  const { pathname } = useLocation();
  const teamUsageReportUiEnabled = useIsFeatureFlagEnabled('team_usage_report_ui');
  const companyBillingEnabled = useIsFeatureFlagEnabled('company_billing');
  // The Reporting parent contains Billing and Team Usage, each gated by an
  // independent feature flag. Filter children per-flag so a user with only
  // one of the two flags still sees the relevant sub-section. Drop the
  // Reporting parent entirely when neither child is visible, matching the
  // pre-split behavior for users without `team_usage_report_ui`.
  const sidebarConfig = getSidebarConfig()
    .map((item) => {
      if (item.title !== 'Reporting') return item;
      const visibleChildren = (item.children || []).filter((child) => {
        if (child.title === 'Billing') return companyBillingEnabled;
        if (child.title === 'Team Usage') return teamUsageReportUiEnabled;
        return true;
      });
      return { ...item, children: visibleChildren };
    })
    .filter(
      (item) => !(item.title === 'Reporting' && (!item.children || item.children.length === 0))
    );

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: '100%',
        '& .simplebar-content': { height: '100%', display: 'flex', flexDirection: 'column' }
      }}
    >
      <Box sx={{ py: 3, textAlign: 'center' }}>
        <Box component={RouterLink} to="/" sx={{ display: 'inline-flex' }}>
          <Logo />
        </Box>
      </Box>

      <LoginBox />
      <NavSection navConfig={sidebarConfig} />
      <Box sx={{ flexGrow: 1 }} />
      <VersionFooter />
    </Scrollbar>
  );

  return (
    <RootStyle>
      <MHidden width="lgUp">
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: { width: DRAWER_WIDTH }
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>

      <MHidden width="lgDown">
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: 'background.paper'
            }
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>
    </RootStyle>
  );
}
